import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { getAppSettings, updateAppConfig } from '../api/appConfigApi';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';

const AppConfigSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    appversionThresholdAndroid: '',
    appversionThresholdIOS: '',
    appversionMessage: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await getAppSettings();
      if (response?.isSuccess && response?.value?.settings) {
        const { settings: apiSettings } = response.value;
        setSettings({
          appversionThresholdAndroid: apiSettings.appversionThresholdAndroid || '',
          appversionThresholdIOS: apiSettings.appversionThresholdIOS || '',
          appversionMessage: apiSettings.appversionMessage || '',
        });
      } else {
        toast.error('Ayarlar yüklenirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Ayarlar yüklenirken hata:', error);
      toast.error('Ayarlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (event) => {
    setSettings((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = async () => {
    // Validation
    if (!settings.appversionThresholdAndroid.trim()) {
      toast.warning('Android versiyon alanı boş olamaz.');
      return;
    }
    if (!settings.appversionThresholdIOS.trim()) {
      toast.warning('iOS versiyon alanı boş olamaz.');
      return;
    }
    if (!settings.appversionMessage.trim()) {
      toast.warning('Güncelleme mesajı boş olamaz.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        appversionThresholdAndroid: settings.appversionThresholdAndroid,
        appversionThresholdIOS: settings.appversionThresholdIOS,
        appversionMessage: settings.appversionMessage,
      };

      const response = await updateAppConfig(payload);
      
      if (response?.isSuccess) {
        toast.success('Ayarlar başarıyla güncellendi.');
      } else {
        toast.error(response?.error?.errors?.[0] || 'Ayarlar güncellenirken bir hata oluştu.');
      }
    } catch (error) {
      console.error('Ayarlar güncellenirken hata:', error);
      toast.error('Ayarlar güncellenirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 3 }}>
      <Card>
        <CardHeader
          title="Mobil Uygulama Ayarları"
          subheader=""
          action={<>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchSettings}
              disabled={loading}
            >
              Yenile
            </Button>
            <br></br><br></br></>
          }
        />
        <Divider />
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Android Versiyon"
                variant="outlined"
                value={settings.appversionThresholdAndroid}
                onChange={handleChange('appversionThresholdAndroid')}
                placeholder="Örn: 1.3.0"
                helperText=""
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="iOS Versiyon"
                variant="outlined"
                value={settings.appversionThresholdIOS}
                onChange={handleChange('appversionThresholdIOS')}
                placeholder="Örn: 1.3.0"
                helperText=""
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Güncelleme Mesajı"
                variant="outlined"
                multiline
                rows={4}
                value={settings.appversionMessage}
                onChange={handleChange('appversionMessage')}
                placeholder="Kullanıcılara gösterilecek güncelleme mesajı"
                helperText="Uygulama güncelleme gerektiğinde kullanıcılara gösterilecek mesaj"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              size="large"
            >
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </Box>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Bilgi
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Bu ayarlar mobil uygulamanın minimum desteklenen versiyonunu belirler. 
              Kullanıcılar belirtilen versiyonun altında bir uygulama kullandığında, 
              güncelleme yapmaları için uyarı mesajı gösterilir.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AppConfigSettings;
